import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/api/client'
import { useWorkspace } from '@/workspaces/WorkspaceContext'
import type { ProjectFlat, ProjectState } from './useProjects'

export type CreateProjectInput = {
  name?: string
  description?: string
  state?: ProjectState
  owner?: string | null
  start_date?: string
  end_date?: string
  customAttributes?: Record<string, unknown>
}

export type UpdateProjectInput = Partial<ProjectFlat> & {
  customAttributes?: Record<string, unknown>
}

function toProjectPayload(input: CreateProjectInput | UpdateProjectInput): Record<string, unknown> {
  const out: Record<string, unknown> = {}

  if ('name' in input && input.name !== undefined) out.name = input.name
  if ('description' in input && input.description !== undefined) out.description = input.description
  if ('state' in input && input.state !== undefined) out.state = input.state
  if ('owner' in input && input.owner !== undefined) out.owner = input.owner
  if ('start_date' in input && input.start_date !== undefined) out.start_date = input.start_date
  if ('end_date' in input && input.end_date !== undefined) out.end_date = input.end_date

  if ('customAttributes' in input && input.customAttributes) {
    Object.assign(out, input.customAttributes)
  }

  return out
}

export function useProjectMutations() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const slug = workspace.slug

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['projects', slug] })
    qc.invalidateQueries({ queryKey: ['project'] })
  }

  const createProject = useMutation({
    mutationFn: (input: CreateProjectInput) =>
      fetchWithAuth<ProjectFlat>(`/api/v1/workspaces/${slug}/projects`, {
        method: 'POST',
        body: JSON.stringify(toProjectPayload(input)),
      }),
    onSuccess: () => invalidate(),
  })

  const updateProject = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateProjectInput }) =>
      fetchWithAuth<ProjectFlat>(`/api/v1/workspaces/${slug}/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(toProjectPayload(patch)),
      }),
    onSuccess: () => invalidate(),
  })

  const archiveProject = useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth(`/api/v1/workspaces/${slug}/projects/${id}/archive`, { method: 'POST' }),
    onSuccess: () => invalidate(),
  })

  return { createProject, updateProject, archiveProject }
}
